import { useState } from 'react';
import { useI18n } from '../../i18n/I18nContext.js';
import { staffApi } from '../../api/resources.js';
import { ApiError } from '../../lib/apiClient.js';
import type { AdminStaff } from '../../api/types.js';
import { Modal } from '../../components/Modal.js';
import { Banner } from '../../components/Banner.js';
import { STAFF_PIN_PATTERN } from '../../validation/staffLimits.js';

export interface SetPinModalProps {
  staff: AdminStaff;
  onClose(): void;
}

/**
 * The PIN is write-only everywhere in this system: it is never returned by
 * the API and this modal never shows a current value, only two blank fields
 * to set a new one.
 */
export function SetPinModal({ staff, onClose }: SetPinModalProps) {
  const { t } = useI18n();
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const valid = STAFF_PIN_PATTERN.test(pin) && pin === confirm;

  async function submit() {
    if (!valid) return;
    setSaving(true);
    setError(null);
    try {
      await staffApi.setPin(staff.id, { pin });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={`${t('staff.setPin')} — ${staff.displayName}`} onClose={onClose} busy={saving}>
      <p className="field-hint">{t('staff.pinNeverShown')}</p>
      {error && <Banner kind="danger">{error}</Banner>}
      {done ? (
        <>
          <Banner kind="ok">{t('staff.pinSet')}</Banner>
          <div className="modal__actions">
            <button type="button" className="btn btn--primary" onClick={onClose}>
              {t('common.close')}
            </button>
          </div>
        </>
      ) : (
        <>
          <label htmlFor="set-pin">
            {t('staff.pin')}
            <input
              id="set-pin"
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              autoFocus
            />
            {pin && !STAFF_PIN_PATTERN.test(pin) && <span className="field-error">{t('staff.pinInvalid')}</span>}
          </label>
          <label htmlFor="set-pin-confirm">
            {t('staff.pinConfirm')}
            <input
              id="set-pin-confirm"
              type="password"
              inputMode="numeric"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {confirm && pin !== confirm && <span className="field-error">{t('staff.pinMismatch')}</span>}
          </label>
          <p className="field-hint">{t('staff.pinHashedNotice')}</p>
          <div className="modal__actions">
            <button type="button" className="btn btn--secondary" onClick={onClose} disabled={saving}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn--primary" disabled={!valid || saving} onClick={() => void submit()}>
              {t('staff.setPin')}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
